// What this device remembers of its Polaroids: how it took each one (the
// date and the light of that finish), and which stops' moments and reveals
// it has already shown. Device-local on purpose, as tree-memory.ts: whether a
// stop is done is always recomputed from progress, so losing this costs a
// date and an animation, never a photo. Async, because the phone keeps it in
// AsyncStorage behind the localStorage polyfill.
import { SEASONS, type Season } from "@aybuben/core/progress/tree.ts";

import type { DayPart } from "../tree/scene.ts";
import { isStrs } from "./tree-memory.ts";

export interface Taken {
	at: number;
	season: Season;
	part: DayPart;
}

export interface PolaroidMemory {
	/** Monotone: a stop once seen done stays here, so its moment fires once. */
	seenDone: string[];
	/** Monotone: stops whose next place card has played its reveal. */
	revealed: string[];
	/** Only finishes made on this device; a synced or backfilled photo has none. */
	taken: Record<string, Taken>;
}

export interface PolaroidMemoryStore {
	/** Null when this device has never initialised the memory for this learner. */
	load(userId: string): Promise<PolaroidMemory | null>;
	save(userId: string, m: PolaroidMemory): Promise<void>;
}

export const polaroidMemoryKey = (userId: string): string => `aybuben:polaroids:${userId}`;

const PARTS: ReadonlySet<unknown> = new Set<DayPart>(["dawn", "day", "dusk", "night"]);

function isTaken(x: unknown): x is Taken {
	if (typeof x !== "object" || x === null) return false;
	const t = x as Record<string, unknown>; // narrowed to an object above
	return (
		typeof t.at === "number" &&
		Number.isFinite(t.at) &&
		SEASONS.includes(t.season as Season) && // includes() only compares; a non-season is simply absent
		PARTS.has(t.part)
	);
}

/** Part by part: a damaged date costs that one date; a damaged list never causes a burst of moments. */
export function parsePolaroidMemory(raw: string | null): PolaroidMemory | null {
	if (raw === null) return null;
	let o: unknown;
	try {
		o = JSON.parse(raw);
	} catch {
		return null;
	}
	if (typeof o !== "object" || o === null) return null;
	const r = o as Record<string, unknown>; // narrowed to an object above
	if (!isStrs(r.seenDone)) return null;
	const taken: Record<string, Taken> = {};
	if (typeof r.taken === "object" && r.taken !== null)
		for (const [k, v] of Object.entries(r.taken)) if (isTaken(v)) taken[k] = v;
	return {
		seenDone: r.seenDone,
		revealed: isStrs(r.revealed) ? r.revealed : [...r.seenDone],
		taken,
	};
}

/** Degrades to "never initialised" when storage is blocked (private mode, site data off). */
export const localPolaroidMemory: PolaroidMemoryStore = {
	async load(userId) {
		let raw: string | null = null;
		try {
			raw = globalThis.localStorage?.getItem(polaroidMemoryKey(userId)) ?? null;
		} catch {
			/* never initialised */
		}
		return parsePolaroidMemory(raw);
	},
	async save(userId, m) {
		try {
			globalThis.localStorage?.setItem(polaroidMemoryKey(userId), JSON.stringify(m));
		} catch {
			/* remembered for this session only */
		}
	},
};
